import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Eye,
  FileText,
  LogOut,
  Pill,
  RefreshCw,
  ShieldCheck,
  Stethoscope
} from 'lucide-react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import toast from 'react-hot-toast'

import DocumentPreviewDialog from '@/components/DocumentPreviewDialog'
import MedicalConsultationPanel from '@/components/MedicalConsultationPanel'
import UnifiedMedicalTimeline from '@/components/UnifiedMedicalTimeline'
import { useAuth } from '@/context/AuthContext'
import { listDoctorAccessesForDoctor } from '@/lib/doctor-access'
import { fetchHealthEntries } from '@/lib/health'
import { getLabResults } from '@/lib/lab-results'
import {
  formatMedicalDocumentDate,
  getMedicalDocumentClinicalDate,
  getMedicalDocumentDoctor,
  getMedicalDocumentSummary,
  inferMedicalDocumentSpecialty
} from '@/lib/medical-document-presentation'
import {
  resolveMedicalDocumentPreviewUrl,
  resolveMedicalDocumentUrl
} from '@/lib/medical-document-storage'
import { getMedicalDocuments } from '@/lib/medical-documents'
import {
  getActiveMedications,
  getMedicationStatusColor,
  getMedicationStatusLabel,
  getMedications,
  getPatientMedicationHistory
} from '@/lib/medications'
import type {
  ActiveMedication,
  DoctorAccessGrant,
  HealthEntry,
  LabResult,
  MedicalDocument,
  Medication,
  MedicationHistory
} from '@/types/health'

type DoctorTab = 'consultation' | 'labs' | 'treatments' | 'documents'

interface DoctorSnapshot {
  healthEntries: HealthEntry[]
  medications: Medication[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
  documents: MedicalDocument[]
  labResults: LabResult[]
  latestEntry: HealthEntry | null
}

interface PreviewDocumentState {
  title: string
  subtitle?: string | null
  fileUrl?: string | null
}

const TABS: Array<{ id: DoctorTab; label: string }> = [
  { id: 'consultation', label: 'Consultation' },
  { id: 'labs', label: 'Analyses biologiques' },
  { id: 'treatments', label: 'Traitements' },
  { id: 'documents', label: 'Documents medicaux' }
]

const CHART_COLORS = ['#38bdf8', '#22c55e', '#f59e0b']

function formatDate(value: string | null | undefined) {
  if (!value) {
    return 'Non renseigne'
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return format(parsed, 'd MMM yyyy', { locale: fr })
}

function getTopLabTests(results: LabResult[], limit = 3) {
  const counts = new Map<string, number>()

  results.forEach((result) => {
    counts.set(result.test_name, (counts.get(result.test_name) || 0) + 1)
  })

  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, limit)
    .map(([testName]) => testName)
}

function buildLabChartData(results: LabResult[], selectedTests: string[]) {
  const rows = new Map<string, Record<string, string | number | null>>()

  results
    .filter((result) => selectedTests.includes(result.test_name) && result.result_value !== null)
    .slice()
    .reverse()
    .forEach((result) => {
      const row = rows.get(result.test_date) || { date: result.test_date }
      row[result.test_name] = result.result_value
      rows.set(result.test_date, row)
    })

  return [...rows.values()].sort((left, right) => String(left.date).localeCompare(String(right.date)))
}

export default function SecureDoctorView() {
  const { user, profile, loading, signOut } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<DoctorTab>('consultation')
  const [selectedGrantId, setSelectedGrantId] = useState('')
  const [selectedSpecialty, setSelectedSpecialty] = useState('')
  const [previewDocument, setPreviewDocument] = useState<PreviewDocumentState | null>(null)

  const metadataRole = user?.user_metadata?.role
  const effectiveRole =
    profile?.role ||
    (metadataRole === 'doctor' || metadataRole === 'admin' || metadataRole === 'patient'
      ? metadataRole
      : null)

  const { data: grants = [], isLoading: grantsLoading, isFetching: grantsFetching, refetch: refetchGrants } = useQuery({
    queryKey: ['doctor-accesses-for-doctor', user?.id],
    queryFn: () => listDoctorAccessesForDoctor(user!.id),
    enabled: Boolean(user) && (effectiveRole === 'doctor' || effectiveRole === 'admin')
  })

  useEffect(() => {
    if (!grants.length) {
      setSelectedGrantId('')
      return
    }

    if (!selectedGrantId || !grants.some((grant) => grant.id === selectedGrantId)) {
      setSelectedGrantId(grants[0].id)
    }
  }, [grants, selectedGrantId])

  const selectedGrant: DoctorAccessGrant | null =
    grants.find((grant) => grant.id === selectedGrantId) || grants[0] || null
  const patientId = selectedGrant?.patient_id || null

  const { data: snapshot, isLoading: snapshotLoading, isFetching: snapshotFetching, refetch: refetchSnapshot } = useQuery({
    queryKey: ['doctor-snapshot', patientId],
    queryFn: async (): Promise<DoctorSnapshot> => {
      if (!patientId) {
        throw new Error('Aucun patient selectionne')
      }

      const [healthEntries, medications, activeMedications, medicationHistory, documents, labResults] = await Promise.all([
        fetchHealthEntries(patientId, 180),
        getMedications(patientId),
        getActiveMedications(patientId),
        getPatientMedicationHistory(patientId),
        getMedicalDocuments(patientId, undefined, 120),
        getLabResults(patientId, 1000)
      ])

      return {
        healthEntries,
        medications,
        activeMedications,
        medicationHistory,
        documents,
        labResults,
        latestEntry: healthEntries[0] || null
      }
    },
    enabled: Boolean(patientId)
  })

  const documents = snapshot?.documents || []
  const medications = snapshot?.medications || []
  const activeMedications = snapshot?.activeMedications || []
  const medicationHistory = snapshot?.medicationHistory || []
  const topLabTests = getTopLabTests(snapshot?.labResults || [])
  const labChartData = buildLabChartData(snapshot?.labResults || [], topLabTests)
  const abnormalResults = (snapshot?.labResults || []).filter((result) => result.is_abnormal).slice(0, 8)
  const visibleDocuments = documents.filter((document) => {
    if (!selectedSpecialty) {
      return true
    }

    return inferMedicalDocumentSpecialty(document) === selectedSpecialty
  })
  const documentSpecialties = [...new Set(documents.map((document) => inferMedicalDocumentSpecialty(document)))]
    .sort((left, right) => left.localeCompare(right, 'fr'))

  async function handleLogout() {
    try {
      await signOut()
      toast.success('Deconnexion medecin reussie')
      navigate('/doctor-access')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Deconnexion impossible')
    }
  }

  async function openDocument(document: MedicalDocument) {
    const clinicalDate = getMedicalDocumentClinicalDate(document)
    const fileUrl = await resolveMedicalDocumentPreviewUrl(document)

    setPreviewDocument({
      title: document.title || 'Document medical',
      subtitle: `${clinicalDate.label} ${formatMedicalDocumentDate(clinicalDate.rawDate)}`,
      fileUrl
    })
  }

  async function openLabResultDocumentPreview(result: LabResult) {
    if (!result.document_url) {
      toast.error('Aucun PDF disponible pour ce resultat')
      return
    }

    const fileUrl = await resolveMedicalDocumentUrl(result.document_url)

    if (!fileUrl) {
      toast.error('Impossible d ouvrir ce PDF pour le moment')
      return
    }

    setPreviewDocument({
      title: result.source_document_title || result.test_name,
      subtitle: `${formatDate(result.test_date)} - ${result.test_name}`,
      fileUrl
    })
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-bg">
        <div className="spinner" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/doctor-access" replace />
  }

  if (effectiveRole !== 'doctor' && effectiveRole !== 'admin') {
    return <Navigate to="/" replace />
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <header className="border-b border-dark-border bg-dark-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 lg:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
                <Stethoscope className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">Interface medecin</h1>
                <p className="text-sm text-slate-400">Vue clinique separee, sans factures.</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  void refetchGrants()
                  void refetchSnapshot()
                }}
                className="btn-secondary px-3"
                disabled={grantsFetching || snapshotFetching}
              >
                <RefreshCw className={`h-4 w-4 ${(grantsFetching || snapshotFetching) ? 'animate-spin' : ''}`} />
              </button>

              <button type="button" onClick={handleLogout} className="btn-secondary">
                <LogOut className="h-4 w-4" />
                Deconnexion
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-4 py-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_220px_minmax(0,0.9fr)]">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Patiente</p>
                {grantsLoading ? (
                  <p className="mt-2 text-sm text-slate-400">Chargement...</p>
                ) : grants.length > 0 ? (
                  <select
                    value={selectedGrant?.id || ''}
                    onChange={(event) => setSelectedGrantId(event.target.value)}
                    className="input mt-2"
                  >
                    {grants.map((grant) => (
                      <option key={grant.id} value={grant.id}>
                        {grant.patient_display_name || 'Patiente'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-2 text-sm text-slate-400">Aucun acces actif</p>
                )}
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Acces</p>
                <p className="mt-3 text-sm text-slate-200">{selectedGrant?.doctor_specialty || 'Suivi general'}</p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-2xl border border-dark-border bg-dark-surface px-3 py-3">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Analyses</p>
                  <p className="mt-2 text-lg font-semibold text-white">{abnormalResults.length}</p>
                  <p className="text-xs text-slate-400">anormales</p>
                </div>

                <div className="rounded-2xl border border-dark-border bg-dark-surface px-3 py-3">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Docs</p>
                  <p className="mt-2 text-lg font-semibold text-white">{documents.length}</p>
                  <p className="text-xs text-slate-400">partages</p>
                </div>

                <div className="rounded-2xl border border-dark-border bg-dark-surface px-3 py-3">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Traitements</p>
                  <p className="mt-2 text-lg font-semibold text-white">{activeMedications.length}</p>
                  <p className="text-xs text-slate-400">actifs</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-cyan-500 bg-cyan-500/15 text-cyan-100'
                    : 'border-dark-border bg-dark-surface-high text-slate-300 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 lg:px-6">
        {grantsLoading || snapshotLoading ? (
          <div className="card flex items-center justify-center py-16">
            <div className="spinner" />
          </div>
        ) : !selectedGrant || !snapshot ? (
          <div className="card py-12 text-center">
            <ShieldCheck className="mx-auto h-12 w-12 text-slate-600" />
            <h2 className="mt-4 text-lg font-semibold text-white">Aucun acces actif</h2>
            <p className="mt-2 text-sm text-slate-400">
              La patiente doit creer un acces depuis ses parametres.
            </p>
          </div>
        ) : (
          <>
            {activeTab === 'consultation' && (
              <div className="space-y-6">
                <UnifiedMedicalTimeline
                  documents={documents}
                  labResults={snapshot.labResults}
                  healthEntries={snapshot.healthEntries}
                  medicationHistory={medicationHistory}
                  onPreviewDocument={(document) => {
                    void openDocument(document)
                  }}
                  title="Chronologie partagee"
                  description="Documents, biologie, journal et traitements utiles au rendez-vous."
                  limit={14}
                />

                <MedicalConsultationPanel
                  documents={documents}
                  labResults={snapshot.labResults}
                  healthEntries={snapshot.healthEntries}
                  activeMedications={activeMedications}
                  medicationHistory={medicationHistory}
                  patientName={selectedGrant.patient_display_name || 'Patiente'}
                  onPreviewDocument={(document) => {
                    void openDocument(document)
                  }}
                />
              </div>
            )}

            {activeTab === 'labs' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-lg font-semibold text-white">Analyses biologiques</h2>
                  {labChartData.length > 0 ? (
                    <div className="mt-6 h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={labChartData}>
                          <CartesianGrid stroke="#23324c" strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            stroke="#94a3b8"
                            fontSize={12}
                            tickFormatter={(value) => formatDate(String(value))}
                          />
                          <YAxis stroke="#94a3b8" fontSize={12} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#0f172a',
                              borderColor: '#1e293b',
                              borderRadius: 14
                            }}
                            labelFormatter={(value) => formatDate(String(value))}
                          />
                          {topLabTests.map((testName, index) => (
                            <Line
                              key={testName}
                              type="monotone"
                              dataKey={testName}
                              stroke={CHART_COLORS[index % CHART_COLORS.length]}
                              strokeWidth={2.5}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="mt-6 rounded-2xl border border-dark-border bg-dark-surface-high p-6 text-sm text-slate-400">
                      Aucune courbe disponible.
                    </div>
                  )}
                </div>

                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-dark-border">
                          <th className="py-3 pr-4 text-left text-slate-300">Date</th>
                          <th className="py-3 pr-4 text-left text-slate-300">Analyse</th>
                          <th className="py-3 pr-4 text-left text-slate-300">Valeur</th>
                          <th className="py-3 pr-4 text-left text-slate-300">Document</th>
                        </tr>
                      </thead>
                      <tbody>
                        {snapshot.labResults.slice(0, 24).map((result) => (
                          <tr key={result.id} className="border-b border-dark-border/60">
                            <td className="py-3 pr-4 text-slate-200">{formatDate(result.test_date)}</td>
                            <td className="py-3 pr-4 text-white">{result.test_name}</td>
                            <td className={`py-3 pr-4 ${result.is_abnormal ? 'text-orange-300' : 'text-emerald-300'}`}>
                              {[result.result_text || result.result_value, result.unit].filter(Boolean).join(' ')}
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex items-center justify-between gap-3">
                                <span className="max-w-[18rem] truncate text-slate-400">
                                  {result.source_document_title || 'Document medical'}
                                </span>
                                {result.document_url && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void openLabResultDocumentPreview(result)
                                    }}
                                    className="rounded-lg border border-dark-border bg-dark-surface p-2 text-slate-400 transition-colors hover:text-white"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'treatments' && (
              <div className="space-y-4">
                {medications.length === 0 ? (
                  <div className="card p-6 text-sm text-slate-400">Aucun traitement partage.</div>
                ) : (
                  medications.map((medication) => (
                    <div key={medication.id} className="card">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-lg font-semibold text-white">{medication.name}</p>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] ${getMedicationStatusColor(medication.status)}`}>
                          {getMedicationStatusLabel(medication.status)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-300">
                        {[medication.dosage, medication.frequency].filter(Boolean).join(' - ') || 'Posologie non renseignee'}
                      </p>
                      <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-slate-400 md:grid-cols-3">
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Debut</p>
                          <p className="mt-1 text-slate-200">{formatDate(medication.start_date)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Prescripteur</p>
                          <p className="mt-1 text-slate-200">{medication.prescriber_name || 'Non renseigne'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Fin</p>
                          <p className="mt-1 text-slate-200">{formatDate(medication.actual_end_date || medication.planned_end_date)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-6">
                <div className="card">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-white">Documents medicaux</h2>
                      <p className="text-sm text-slate-400">Filtre simple par specialite.</p>
                    </div>
                    <select
                      value={selectedSpecialty}
                      onChange={(event) => setSelectedSpecialty(event.target.value)}
                      className="input lg:w-72"
                    >
                      <option value="">Toutes les specialites</option>
                      {documentSpecialties.map((specialty) => (
                        <option key={specialty} value={specialty}>
                          {specialty}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {visibleDocuments.length === 0 ? (
                  <div className="card p-6 text-sm text-slate-400">Aucun document pour cette selection.</div>
                ) : (
                  visibleDocuments.map((document) => {
                    const clinicalDate = getMedicalDocumentClinicalDate(document)

                    return (
                      <div key={document.id} className="card">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-lg font-semibold text-white">{document.title || 'Document medical'}</p>
                              <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-[11px] text-cyan-100">
                                {inferMedicalDocumentSpecialty(document)}
                              </span>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-slate-400 md:grid-cols-3">
                              <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{clinicalDate.label}</p>
                                <p className="mt-1 text-slate-200">{formatMedicalDocumentDate(clinicalDate.rawDate)}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Praticien</p>
                                <p className="mt-1 text-slate-200">{getMedicalDocumentDoctor(document) || 'Non renseigne'}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Type</p>
                                <p className="mt-1 text-slate-200">{inferMedicalDocumentSpecialty(document)}</p>
                              </div>
                            </div>

                            <div className="mt-4 rounded-2xl border border-dark-border bg-dark-surface-high p-4 text-sm text-slate-300">
                              {getMedicalDocumentSummary(document)}
                            </div>
                          </div>

                          {document.file_url && (
                            <button
                              type="button"
                              onClick={() => {
                                void openDocument(document)
                              }}
                              className="btn-secondary h-fit"
                            >
                              <Eye className="h-4 w-4" />
                              Apercu PDF
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </>
        )}
      </main>

      {previewDocument && (
        <DocumentPreviewDialog
          title={previewDocument.title}
          subtitle={previewDocument.subtitle}
          fileUrl={previewDocument.fileUrl}
          onClose={() => setPreviewDocument(null)}
        />
      )}
    </div>
  )
}
